import { Box, Button, List, ListItem, styled, useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Tooltip from 'components/tooltip';
import { useAccountState } from 'contexts/account/account-context';
import { Check, CheckCheck, MoveRight, TriangleAlert } from 'lucide-react';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import googleTagManager from 'utils/google-tag-manager';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import { SiteListItem } from 'views/order-feed/features/my-supply-plan/types/ofd-sites.type';
import { SiteTab } from 'views/order-feed/features/my-supply-plan/types/site-tab.enum';

type WeekSupplyProps = {
  week: number;
};

const WeekSupply = styled(Box)<WeekSupplyProps>(({ theme, week }) => ({
  background: theme.palette.lightGray.light,
  height: 45,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-around',
  alignItems: 'center',
  paddingLeft: week === 1 ? '15px' : '', // adjusts lopsided padding for first week, especially noticeable when week has 3 rows
  borderRadius: week === 1 ? '100px 0 0 100px' : week === 2 ? '0 0px 0 0' : '0px 100px 100px 0px',
}));

type Props = {
  site: SiteListItem;
};

const PAGE_NAME_ID = 'orderFeed.listOrderSupplyPlan';

type SiteRowBaseProps = Props & {
  isMobile: boolean;
  onSelect: () => void;
};

type ActiveSiteRowProps = SiteRowBaseProps & {
  overviewIsPending: boolean;
  ofd2SiteEnabled: boolean | undefined;
};

const convertToTons = (value: number) => {
  const tons = value / 1000;
  return tons % 1 === 0 ? tons : tons.toFixed(2);
};

const renderSiteActionCell = (isMobile: boolean, onSelect: () => void) => (
  <ListCell align="right" sx={{ width: { xs: '100%', md: 'auto' } }}>
    <Button fullWidth={isMobile} size="small" onClick={onSelect}>
      <FormattedMessage id="general.view" />
    </Button>
  </ListCell>
);

const renderOrderStatusCell = (site: SiteListItem) => {
  if (!site.isOfd1Enabled && !site.isOfd2Enabled) return <ListCell align="center" />;

  const ofdVersion = site.isOfd2Enabled ? '2.0' : '1.0';
  const tooltipId = site.hasAutomaticOrderingOfd2
    ? `${PAGE_NAME_ID}.siteList.ofdAutomaticOrderingTooltip`
    : `${PAGE_NAME_ID}.siteList.ofdEnabledTooltip`;

  return (
    <ListCell align="center">
      <Tooltip title={<FormattedMessage id={tooltipId} values={{ ofdVersion }} />}>
        <Box display="flex" justifyContent="center">
          {site.hasAutomaticOrderingOfd2 ? <CheckCheck /> : <Check />}
        </Box>
      </Tooltip>
    </ListCell>
  );
};

const renderAlertCell = (site: SiteListItem, theme: Theme) => {
  const warningMessages = [
    site.productsNotAvailable && `${PAGE_NAME_ID}.siteList.productNotAvailable`,
    site.scheduleTooShort && `${PAGE_NAME_ID}.siteList.scheduleTooShort`,
  ].filter((message): message is string => Boolean(message));

  return (
    <ListCell sx={{ textAlign: 'center' }}>
      {warningMessages.length > 0 && (
        <Tooltip
          title={
            <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column' }}>
              {warningMessages.map((message) => (
                <ListItem
                  key={message}
                  disablePadding
                  sx={{ display: 'list-item', listStyleType: 'disc', listStylePosition: 'inside' }}>
                  <FormattedMessage id={message} />
                </ListItem>
              ))}
            </List>
          }>
          <TriangleAlert
            style={{ display: 'flex' }}
            fill={theme.palette.warning.main}
            strokeWidth={1}
            stroke={theme.palette.darkBlue.main}
          />
        </Tooltip>
      )}
    </ListCell>
  );
};

const renderWeekSupplyCell = (deliveries: number[], week: number, theme: Theme) => (
  <ListCell key={`week${week}`} sx={{ whiteSpace: 'break-spaces !important', padding: 0 }}>
    <WeekSupply week={week}>
      {deliveries.length > 0 ? deliveries.map((value) => convertToTons(value)).join(' t \n') : deliveries[0]}
      &nbsp;t
      {week === 3 ? null : <MoveRight strokeWidth={2} stroke={theme.palette.darkBlue.o25} />}
    </WeekSupply>
  </ListCell>
);

const renderDeliveryCells = (site: SiteListItem, theme: Theme) => (
  <>
    {renderWeekSupplyCell(site.week1Deliveries, 1, theme)}
    {renderWeekSupplyCell(site.week2Deliveries, 2, theme)}
    {renderWeekSupplyCell(site.week3Deliveries, 3, theme)}
  </>
);

const BasicSiteListRow: React.FC<SiteRowBaseProps> = ({ site, isMobile, onSelect }) => (
  <ListRow onClick={onSelect}>
    <ListCell sx={{ whiteSpace: 'break-spaces !important' }}>{site.siteName}</ListCell>
    {renderSiteActionCell(isMobile, onSelect)}
  </ListRow>
);

const StandardSiteListRow: React.FC<ActiveSiteRowProps> = ({ site, isMobile, onSelect, overviewIsPending, ofd2SiteEnabled }) => {
  const theme = useTheme();
  const overviewCells = overviewIsPending ? (
    renderOrderStatusCell(site)
  ) : (
    <>
      {renderAlertCell(site, theme)}
      {renderOrderStatusCell(site)}
      {ofd2SiteEnabled && (
        <ListCell align="center" sx={{ whiteSpace: 'break-spaces !important' }}>
          {site.inventory ? `${convertToTons(site.inventory)} t` : ''}
        </ListCell>
      )}
      <ListCell align="center" sx={{ whiteSpace: 'break-spaces !important' }}>
        {site.weeklyConsumption ? `${convertToTons(site.weeklyConsumption)} t` : ''}
      </ListCell>
      {renderDeliveryCells(site, theme)}
      <ListCell align="center" sx={{ whiteSpace: 'break-spaces !important' }}>
        {site.siteIntensity && (
          <FormattedNumber
            value={site.siteIntensity * 100}
            unit="percent"
            style="unit"
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        )}
      </ListCell>
    </>
  );

  return (
    <ListRow onClick={onSelect}>
      <ListCell sx={{ whiteSpace: 'break-spaces !important' }}>{site.siteName}</ListCell>
      {overviewCells}
      {renderSiteActionCell(isMobile, onSelect)}
    </ListRow>
  );
};

const SiteListRow: React.FC<Props> = ({ site }) => {
  const history = useHistory();
  const theme = useTheme();
  const { siteTab } = useParams<MySupplyPlanParams>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    account: { company },
  } = useAccountState();

  const ofd1SiteEnabled = company?.features.hasOrderFreeDelivery;
  const ofd2SiteEnabled = company?.features.hasOrderFreeDelivery2;
  const hasOrderFreeDelivery = ofd1SiteEnabled || ofd2SiteEnabled;
  const isInactive = siteTab === SiteTab.INACTIVE;

  const {
    siteOverviewQuery: {
      isFetching: overviewIsFetching,
      isLoading: overviewIsLoading,
      isFetched: overviewIsFetched,
    },
  } = useSitesState();

  const overviewIsPending = !overviewIsFetched || overviewIsLoading || overviewIsFetching;

  const handleOFDSelect = () => {
    const alert = [site.scheduleTooShort && 'scheduleTooShort', site.productsNotAvailable && 'productsNotAvailable']
      .filter((message): message is string => Boolean(message))
      .join(', ');

    googleTagManager.push({
      event: 'supplyPlanViewSite',
      alert: alert,
      supplyPlanType: site.isOfd2Enabled ? SupplyPlanType.OFD2 : site.isOfd1Enabled ? SupplyPlanType.OFD1 : SupplyPlanType.MANUAL,
      siteStatus: site.hasRecentOrders ? 'active' : 'inactive',
      companyId: company!.companyId,
      siteId: site.globalSiteId,
      siteErpId: site.siteErpId,
      companyErpId: company!.customerErpId,
      pageUrl: window.location.pathname,
    });

    if (company?.features.hasOrderFreeDelivery2 && site.isOfd2Enabled) {
      history.push(`/order-feed/supply-plan/order-free-delivery/${site.siteErpId}/${site.globalSiteId}/orders`);
    } else history.push(`/order-feed/supply-plan/${site.siteErpId}/${site.globalSiteId}`);
  };

  const rowProps = {
    site,
    isMobile,
    onSelect: handleOFDSelect,
  };

  if (isInactive || !hasOrderFreeDelivery) return <BasicSiteListRow {...rowProps} />;
  return <StandardSiteListRow {...rowProps} overviewIsPending={overviewIsPending} ofd2SiteEnabled={ofd2SiteEnabled} />;
};

export default SiteListRow;
